import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import {
  AppPlan,
  AppPlanService,
  AppPlanFilter,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseAppPlansOptions {
  /** Space ID to filter app plans by */
  spaceId?: string | undefined;

  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<AppPlanFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;
}

export interface UseAppPlansReturn {
  // Data state
  appPlan: AppPlan | null;
  appPlans: AppPlan[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<AppPlanFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<AppPlanFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<AppPlan[]>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Filter operations
  setFilters: (filters: Partial<AppPlanFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  featuredPlans: AppPlan[];

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [
  { key: "app_plans.order_no", direction: "asc" },
];

export const useAppPlans = (
  options: UseAppPlansOptions = {}
): UseAppPlansReturn => {
  const {
    spaceId,
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
  } = options;

  // Use refs for option values to avoid dependency issues
  const spaceIdRef = useRef(spaceId);
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    spaceIdRef.current = spaceId;
  }, [spaceId]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [appPlan, setAppPlan] = useState<AppPlan | null>(null);
  const [appPlans, setAppPlans] = useState<AppPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] = useState<Partial<AppPlanFilter>>(() => {
    const filter = { ...initialFilters };
    return filter;
  });
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => appPlans.length === 0 && !loading,
    [appPlans.length, loading]
  );

  const featuredPlans = useMemo(() => {
    return appPlans.filter((plan) => plan.isPlanFeatured === true);
  }, [appPlans]);

  // Helper function to update app plan in list if it exists, or add it if not
  const updateOrAddAppPlanInList = useCallback((updatedAppPlan: AppPlan) => {
    setAppPlans((prev) => {
      const existingIndex = prev.findIndex((p) => p.id === updatedAppPlan.id);
      if (existingIndex >= 0) {
        const newAppPlans = [...prev];
        newAppPlans[existingIndex] = updatedAppPlan;
        return newAppPlans;
      } else {
        return [updatedAppPlan, ...prev];
      }
    });
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setAppPlan(null);
    setAppPlans([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
  }, []);

  // Get single app plan (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await AppPlanService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const appPlanData = result.data[0] as AppPlan;
          setAppPlan(appPlanData);
          updateOrAddAppPlanInList(appPlanData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load app plan";
          setLastError(errorMessage);
          setAppPlan(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load app plan";
        setLastError(errorMessage);
        setAppPlan(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddAppPlanInList]
  );

  // List app plans (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<AppPlanFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setAppPlans([]);
          setCurrentPage(1);
        }
      }

      const combinedFilter = { ...filter };

      setFilters(combinedFilter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await AppPlanService.list(
          combinedFilter as AppPlanFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newAppPlans: AppPlan[] = Array.isArray(result.data)
            ? result.data
            : [];

          if (isAppending) {
            setAppPlans((prev) => {
              const existingIds = new Set(prev.map((p) => p.id));
              const uniqueNewAppPlans = newAppPlans.filter(
                (p) => !existingIds.has(p.id)
              );
              return [...prev, ...uniqueNewAppPlans];
            });
          } else {
            setAppPlans(newAppPlans);
          }

          setHasMore(newAppPlans.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load app plans";
          setLastError(errorMessage);
          if (!isAppending) setAppPlans([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load app plans";
        setLastError(errorMessage);
        if (!isAppending) setAppPlans([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many app plans by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<AppPlan[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await AppPlanService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedAppPlans: AppPlan[] = Array.isArray(result.data)
            ? result.data
            : [];
          loadedAppPlans.forEach((plan) => {
            updateOrAddAppPlanInList(plan);
          });
          setLastError(null);
          return loadedAppPlans;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load app plans";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load app plans";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddAppPlanInList]
  );

  // Load more app plans
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh app plans
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      const autoLoadFilters = { ...initialFilters };

      list(autoLoadFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    appPlan,
    appPlans,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    get,
    list,
    getMany,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    reset,
    featuredPlans,
    lastError,
    clearError,
  };
};
